import Config from 'config';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import QueryString, { stringify } from 'qs';
import { parseXMLRequestResponse } from '../utils/util';
import { TRequestError, TOnClose, TErrMessage } from './types';
import { IPreviousValidationSlice } from './previousValidationSlice';
import { EXECUTION_FORM } from '../components/apia/execution/FormsController';
import { TApiaLoad, TApiaLoadForm, TApiaSystemMessageObj } from '../types';
import ApiaApi, { parseMessages } from '../api/apiaApi/apiaApi';

export type TFetchConfirmParams = {
  [key: string]: unknown;
};

export type TConfirmFormResponse = {
  onClose: TOnClose;
  sysMessages?: TErrMessage;
  sysExceptions?: TErrMessage;
  pathSelection?: boolean | null;
  groupSelection?: boolean | null;
  load?: {
    type: 'form' | 'text' | 'table' | 'function';
    canClose: boolean;
    text?: {
      closeAll: string;
      title: string;
      addClass: string;
      label: string;
    };
    form?: {
      action: string;
      addClass: string;
      ajaxNewPanel: boolean;
      ajaxsubmit: boolean;
      autoExpand: boolean;
      closeAll: boolean;
      doEscape: boolean;
      multiPart: boolean;
      onLoad: 'pathOnload';
      showErrors: boolean;
      title: string;
      titleClass: string;
      buttons:
        | {
            button: {
              id: string;
              onclick: string;
              text: string;
              type: 'submitAjax';
            };
          }
        | [];
      elements: {
        element: {
          html: boolean;
          id: string;
          name: string;
          onChange: string;
          readonly: boolean;
          selected: boolean;
          text: string;
          title: string;
          type: '2column';
          value: string;
          valueAsAttribute: boolean;
        };
      };
    };
  };
  actions?: {
    action: {
      toDo: 'functionTimedCall';
      param: [number, string];
    };
  };
};

export type TFetchTaskActionResponse = {
  onClose?: TOnClose;
  sysMessages?: TErrMessage;
  sysExceptions?: TErrMessage;
  type?: string;
  actions?: {
    action: {
      param: string[];
      toDo: string;
    };
  };
  load?: {
    canClose: boolean;
    type: string;
    text: {
      label: string;
      closeAll: boolean;
      addClass?: string;
      title?: string;
    };
  };
};

export type TFetchClearEvalResponse = {
  onClose: string;
  result: string;
};

export type TFetchConfirmPoolResponse = {
  onClose: TOnClose;
  sysMessages?: TErrMessage;
  sysExceptions?: TErrMessage;
  type?: string;
  load?: {
    canClose: boolean;
    type: string;
    text: {
      label: string;
    };
  };
};

export type TFetchConfirmPoolParams = {
  action: string;
  params: {
    [key: string]: string | string[] | boolean;
    task: string[];
  };
};
export interface IConfirmFormSlice {
  autoCloseTab: boolean;
  confirmIsLoading?: boolean;
  confirmSubmitSucessMsg: string | null;
  onClose: TOnClose;
  processParameters: TFetchConfirmParams;
  shouldDisplayControls: boolean;
  response?: TApiaSystemMessageObj<TApiaLoad<TApiaLoadForm>> | null;
}

declare global {
  interface Window {
    ENTITY_EXECUTION?: boolean;
  }
}

export const fetchConfirm = createAsyncThunk<
  TApiaSystemMessageObj<TApiaLoad<TApiaLoadForm>>,
  TFetchConfirmParams[],
  {
    state: {
      confirmFormSlice: IConfirmFormSlice;
      previousValidationSlice: IPreviousValidationSlice;
    };
  }
>('confirmFormSlice/fetchConfirm', async (params, thunkAPI) => {
  let action;
  const { submitValues: values } =
    thunkAPI.getState().previousValidationSlice.forms[EXECUTION_FORM];

  try {
    if (Config.ENV === 'dev') {
      action = `${Config.CONFIRM_FORM_URL()}?confirm=false`;
    } else {
      action = `${Config.CONFIRM_FORM_URL()}`;
    }
    const parameters = Object.assign(
      {
        inModal: window.frameElement?.classList.contains('jspModal')
          ? true
          : undefined,
      },
      ...params,
      values,
    ) as TFetchConfirmParams;

    const response =
      window.ENTITY_EXECUTION === true
        ? (
            await ApiaApi.post<TApiaSystemMessageObj<TApiaLoad<TApiaLoadForm>>>(
              action,
              {
                postData: stringify(parameters, { arrayFormat: 'repeat' }),
                handleLoad: true,
              },
            )
          )?.data
        : parseXMLRequestResponse(
            await axios.post(
              action,
              stringify(parameters, { arrayFormat: 'repeat' }),
            ),
          );

    return response as TApiaSystemMessageObj<TApiaLoad<TApiaLoadForm>>;
  } catch (err) {
    const error = (err as TRequestError).response;
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const fetchConfirmPool = createAsyncThunk<
  TApiaSystemMessageObj<TApiaLoad<TApiaLoadForm>>,
  { url: string; postData: string }
>('confirmFormSlice/fetchConfirmPool', async ({ url, postData }, thunkAPI) => {
  try {
    const response = await axios.post(url, postData);
    const parsedResponse = parseXMLRequestResponse(
      response,
    ) as TApiaSystemMessageObj<TApiaLoad<TApiaLoadForm>>;

    parseMessages(parsedResponse);

    return parsedResponse;
  } catch (err) {
    const error = (err as TRequestError).response;
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const fetchClearEvalPath = createAsyncThunk<
  TFetchClearEvalResponse,
  null
>('confirmFormSlice/fetchClearEvalPath', async (_, thunkAPI) => {
  try {
    const response = await axios.post(Config.CLEAR_EVAL_PATH);
    return parseXMLRequestResponse(response) as TFetchClearEvalResponse;
  } catch (err) {
    const error = (err as TRequestError).response;
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const fetchTaskAction = createAsyncThunk<
  TFetchTaskActionResponse | null | undefined,
  string,
  {
    state: {
      confirmFormSlice: IConfirmFormSlice;
      previousValidationSlice: IPreviousValidationSlice;
    };
  }
>('confirmFormSlice/fetchTaskAction', async (params, thunkAPI) => {
  try {
    const { submitValues: values } =
      thunkAPI.getState().previousValidationSlice.forms[EXECUTION_FORM];
    const response = await axios.post(
      params,
      QueryString.stringify(values, { arrayFormat: 'repeat' }),
    );
    return parseXMLRequestResponse(response) as TFetchTaskActionResponse;
  } catch (err) {
    const error = (err as TRequestError).response;
    return thunkAPI.rejectWithValue(error.data);
  }
});

const confirmFormSlice = createSlice({
  name: 'confirmFormSlice',
  initialState: {
    response: null,
    confirmSubmitSucessMsg: null,
    onClose: null,
    autoCloseTab: false,
    confirmIsLoading: false,
    processParameters: {},
    shouldDisplayControls: true,
  } as IConfirmFormSlice,
  reducers: {
    confirmFormResult: (
      state,
      action: PayloadAction<
        Omit<IConfirmFormSlice, 'processParameters' | 'shouldDisplayControls'>
      >,
    ) => {
      state.confirmSubmitSucessMsg = action.payload.confirmSubmitSucessMsg;
      state.shouldDisplayControls = !action.payload.confirmSubmitSucessMsg;
      state.onClose = action.payload.onClose;
      state.autoCloseTab = action.payload.autoCloseTab;
      state.confirmIsLoading = false;
    },
    setConfirmSubmitSucessMsg(state, { payload }: PayloadAction<string>) {
      state.confirmSubmitSucessMsg = payload;
    },
    setProcessParameter(
      state,
      {
        payload: { name, value },
      }: PayloadAction<{ name: string; value: string }>,
    ) {
      state.processParameters[name] = value;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchConfirm.pending, (state, action) => {
      state.confirmIsLoading = true;
      if (action.payload) {
        state.response = action.payload;
      }
    });
    builder.addCase(
      fetchConfirm.fulfilled,
      (
        state,
        action: PayloadAction<TApiaSystemMessageObj<TApiaLoad<TApiaLoadForm>>>,
      ) => {
        state.confirmIsLoading = false;
        if (action.payload) {
          state.response = action.payload;
        }
      },
    );
    builder.addCase(fetchConfirm.rejected, (state) => {
      state.confirmIsLoading = false;
      state.response = null;
    });

    builder.addCase(fetchConfirmPool.pending, (state) => {
      state.confirmIsLoading = true;
      state.response = null;
    });
    builder.addCase(
      fetchConfirmPool.fulfilled,
      (
        state,
        action: PayloadAction<TApiaSystemMessageObj<TApiaLoad<TApiaLoadForm>>>,
      ) => {
        state.confirmIsLoading = false;
        if (action.payload) {
          state.response = action.payload;
        }
      },
    );
    builder.addCase(fetchConfirmPool.rejected, (state) => {
      state.confirmIsLoading = false;
      state.response = null;
    });

    builder.addCase(fetchTaskAction.pending, (state) => {
      state.confirmIsLoading = true;
    });

    builder.addCase(fetchTaskAction.fulfilled, (state) => {
      state.confirmIsLoading = false;
    });
    builder.addCase(fetchTaskAction.rejected, (state) => {
      state.confirmIsLoading = false;
    });
  },
});

export default confirmFormSlice.reducer;
export const {
  confirmFormResult,
  setProcessParameter,
  setConfirmSubmitSucessMsg,
} = confirmFormSlice.actions;
